<template>
    <!--
      One control for what the viewer's left list contains: the Production /
      Design mode, and inside Production the phase and batch filter plus the
      "show ready" switch.

      It is the by-project counterpart of SelectorProductionDate — same 50 px
      bordered sheet, same arrows, same slot. Everything that decides the
      content of the list lives here, so no control can sit on screen doing
      nothing: in Design mode there are no batches, and the dropdown says so by
      holding only the mode toggle.
    -->
    <v-sheet border rounded width="100%" height="50" class="d-flex flex-row">

        <v-btn :color="color" class="my-auto" icon variant="text" :disabled="!has_prev" @click="step(-1)">
            <v-icon>mdi-arrow-left</v-icon>
        </v-btn>

        <v-menu v-model="menu" :close-on-content-click="false" location="bottom" min-width="300" max-width="360">
            <template v-slot:activator="{ props }">
                <v-btn v-bind="props" :color="color" variant="text" height="46"
                    class="my-auto flex-grow-1 text-none px-2 filter-btn" style="min-width: 0">
                    <!-- No glyph here on purpose: the lines already say what the
                         list holds, and icons.md has no "filter" entry to spend
                         on decoration. -->
                    <div class="d-flex flex-row align-center w-100 ga-2">
                        <div class="flex-grow-1 text-left" style="min-width: 0">
                            <!-- Two lines is a production-mode affordance: it
                                 takes two names to place a batch, since batch
                                 names only repeat-check inside their phase. -->
                            <template v-if="!designs_mode">
                                <div class="text-caption text-truncate lines">{{ phase_label }}</div>
                                <div class="text-body-2 text-truncate lines">{{ batch_label }}</div>
                            </template>
                            <div v-else class="text-body-2 text-truncate lines">{{ t("design") }}</div>
                        </div>
                        <template v-if="!designs_mode">
                            <v-chip v-if="isComplete(scope)" size="x-small" variant="tonal" color="success" class="pit-mono">
                                {{ scope.ready }}/{{ scope.total }}
                            </v-chip>
                            <span v-else class="text-caption pit-mono">{{ scope.ready }}/{{ scope.total }}</span>
                        </template>
                    </div>
                </v-btn>
            </template>

            <v-card>
                <!-- Managers only. A production worker has no design mode: the
                     router guard forces it off for them. -->
                <template v-if="show_mode">
                    <v-btn-toggle mandatory color="accent" variant="text" :model-value="designs_mode"
                        @update:model-value="setDesignsMode" class="w-100 d-flex">
                        <v-btn :value="false" class="flex-grow-1" height="48">
                            <span class="text-truncate">{{ t("production") }}</span>
                        </v-btn>
                        <v-btn :value="true" class="flex-grow-1" height="48">
                            <span class="text-truncate">{{ t("design") }}</span>
                        </v-btn>
                    </v-btn-toggle>
                    <v-divider />
                </template>

                <template v-if="!designs_mode">
                    <v-list class="py-0 scroller">
                        <!-- mdi-close is the generic clear action of icons.md
                             §11, which is exactly what this row does. -->
                        <v-list-item :active="!filtered" @click="clear">
                            <template v-slot:prepend>
                                <v-icon>mdi-close</v-icon>
                            </template>
                            <v-list-item-title>{{ t("all") }}</v-list-item-title>
                            <template v-slot:append>
                                <v-chip v-if="isComplete(all)" size="x-small" variant="tonal" color="success" class="pit-mono">
                                    {{ all.ready }}/{{ all.total }}
                                </v-chip>
                                <span v-else class="text-caption text-medium-emphasis pit-mono">
                                    {{ all.ready }}/{{ all.total }}
                                </span>
                            </template>
                        </v-list-item>

                        <v-divider />

                        <template v-for="group in groups" :key="group.phase_uuid">
                            <!-- The phase row is the phase filter, not a header.
                                 It is what keeps every action at two taps. -->
                            <v-list-item class="phase-row" :active="group.phase_uuid == phase_filter && !batch_filter"
                                @click="pickPhase(group)">
                                <template v-slot:prepend>
                                    <!-- Phase, icons.md §5. -->
                                    <v-icon>mdi-timeline-outline</v-icon>
                                </template>
                                <v-list-item-title class="text-body-2 font-weight-medium">
                                    {{ group.phase || t("no_phase") }}
                                </v-list-item-title>
                                <template v-slot:append>
                                    <v-chip v-if="isComplete(group)" size="x-small" variant="tonal" color="success" class="pit-mono">
                                        {{ group.ready }}/{{ group.total }}
                                    </v-chip>
                                    <span v-else class="text-caption text-medium-emphasis pit-mono">
                                        {{ group.ready }}/{{ group.total }}
                                    </span>
                                </template>
                            </v-list-item>

                            <v-list-item v-for="batch in group.batches" :key="batch.uuid" class="pl-8"
                                :active="batch.uuid == batch_filter" :disabled="batch.total == 0"
                                @click="pickBatch(batch)">
                                <template v-slot:prepend>
                                    <!-- Batch, icons.md §5: pit:batch, filled
                                         once every constructible in it is ready. -->
                                    <v-icon>{{ batchIcon(batch) }}</v-icon>
                                </template>
                                <v-list-item-title class="text-body-2">{{ batch.name }}</v-list-item-title>
                                <template v-slot:append>
                                    <v-chip v-if="isComplete(batch)" size="x-small" variant="tonal" color="success" class="pit-mono">
                                        {{ batch.ready }}/{{ batch.total }}
                                    </v-chip>
                                    <span v-else class="text-caption text-medium-emphasis" :class="{ 'pit-mono': batch.total }">
                                        {{ batch.total ? `${batch.ready}/${batch.total}` : t("empty") }}
                                    </span>
                                </template>
                            </v-list-item>
                        </template>
                    </v-list>

                    <v-divider />

                    <!-- Pinned: toggling it must not close the menu. -->
                    <div class="px-4 py-2">
                        <v-switch
                            :label="t('show_ready_constructibles')" :model-value="show_ready"
                            @click="selected_store.toggleShowReadyConstructibles">
                        </v-switch>
                    </div>
                </template>
            </v-card>
        </v-menu>

        <v-btn v-if="filtered" color="accent" class="my-auto" icon variant="text" size="small" @click="clear">
            <v-icon>mdi-close</v-icon>
        </v-btn>

        <v-btn :color="color" class="my-auto" icon variant="text" :disabled="!has_next" @click="step(1)">
            <v-icon>mdi-arrow-right</v-icon>
        </v-btn>

    </v-sheet>
</template>

<style scoped>
/* Two lines inside a 46 px button only fit once the default leading is cut. */
.lines {
    line-height: 1.2;
}

/* A v-btn lays its content out in a centred grid area, so without this the two
   lines shrink to their text width instead of filling the button. */
.filter-btn :deep(.v-btn__content) {
    width: 100%;
}

.scroller {
    max-height: 320px;
    overflow-y: auto;
}

.phase-row {
    background: rgba(var(--v-border-color), var(--v-hover-opacity));
}
</style>

<script setup>
import { computed, ref } from "vue";
import { batchIcon } from "@/plugins/icons";
import { useConstructiblesStore } from "@/stores/constructibles";
import { useSelectedStore } from "@/stores/selected";
import { useSessionStore } from "@/stores/session";
import { useViewerStore } from "@/stores/viewer";
import { usePitStoreBasics } from "@/stores/app_common";

const { t } = usePitStoreBasics();
const constructibles_store = useConstructiblesStore();
const selected_store = useSelectedStore();
const session_store = useSessionStore();
const viewer_store = useViewerStore();

const menu = ref(false);

const designs_mode = computed(() => viewer_store.designs_mode);
const show_mode = computed(() => !session_store.iam_prod_worker);

const groups = computed(() => constructibles_store.myBatchesByPhase);
const phase_filter = computed(() => selected_store.viewer_phase_filter);
const batch_filter = computed(() => selected_store.viewer_batch_filter);
const show_ready = computed(() => selected_store.show_ready_constructibles);
const filtered = computed(() => !designs_mode.value && (!!phase_filter.value || !!batch_filter.value));
const color = computed(() => (filtered.value ? "accent" : "grey"));

// Every non-empty batch in myBatches order (phase, then batch priority). The
// arrows walk this, so stepping past the last batch of a phase lands on the
// first of the next one.
const flat_batches = computed(() => groups.value.flatMap(g => g.batches).filter(b => b.total > 0));
const current_batch = computed(() => flat_batches.value.find(b => b.uuid == batch_filter.value));
const current_index = computed(() => flat_batches.value.findIndex(b => b.uuid == batch_filter.value));

// When no batch is picked yet, the arrows enter the list from either end,
// inside the phase filter when one is set.
const entry_batches = computed(() => {
    if (!phase_filter.value) return flat_batches.value;
    return flat_batches.value.filter(b => b.phase_uuid == phase_filter.value);
});

const has_prev = computed(() => {
    if (designs_mode.value) return false;
    if (current_index.value == -1) return entry_batches.value.length > 0;
    return current_index.value > 0;
});
const has_next = computed(() => {
    if (designs_mode.value) return false;
    if (current_index.value == -1) return entry_batches.value.length > 0;
    return current_index.value < flat_batches.value.length - 1;
});

const all = computed(() => ({
    ready: groups.value.reduce((acc, g) => acc + g.ready, 0),
    total: groups.value.reduce((acc, g) => acc + g.total, 0),
}));

// The counts of exactly what the list is showing.
const scope = computed(() => {
    if (current_batch.value) return current_batch.value;
    if (phase_filter.value) {
        const group = groups.value.find(g => g.phase_uuid == phase_filter.value);
        if (group) return group;
    }
    return all.value;
});

// Everything counted is done. Empty is not done — "all of zero are ready" is
// true and useless, the same rule BatchSerializer.get_status applies, so an
// empty batch reads `empty` rather than turning green.
function isComplete(row) {
    return row.total > 0 && row.ready == row.total;
}

// The filter holds the phase uuid; the label shows its name.
const phase_label = computed(() => {
    if (!phase_filter.value) return t("all_phases");
    return groups.value.find(g => g.phase_uuid == phase_filter.value)?.phase || t("no_phase");
});
const batch_label = computed(() => current_batch.value?.name || t("all_batches"));

function setDesignsMode(mode) {
    viewer_store.setDesignsMode(mode);
    menu.value = false;
}

function pickPhase(group) {
    selected_store.setViewerPhaseFilter(group.phase_uuid);
    menu.value = false;
}

function pickBatch(batch) {
    selected_store.setViewerBatchFilter(batch);
    menu.value = false;
}

function clear() {
    selected_store.clearViewerFilters();
    menu.value = false;
}

function step(delta) {
    let target;
    if (current_index.value == -1) {
        const list = entry_batches.value;
        if (list.length == 0) return;
        target = delta > 0 ? list[0] : list[list.length - 1];
    } else {
        target = flat_batches.value[current_index.value + delta];
    }
    if (target) selected_store.setViewerBatchFilter(target);
}
</script>
